/**
 * Utility functions for integrating VickyAI recommendations with Optimizely CMS content blocks
 */

/**
 * Recursively scan CMS content structure to find VickyProductCarouselBlock types
 * Enhanced with circular reference protection and comprehensive nesting support
 *
 * @param {Object} content - Optimizely CMS content object
 * @returns {Array} Array of VickyProductCarouselBlock objects found in content
 */
export const findVickyCarouselBlocks = (content) => {
    const vickyBlocks = []
    const visited = new WeakSet() // Prevent circular reference issues

    // Helper function to recursively traverse content structure
    const traverse = (obj, path = '') => {
        if (!obj || typeof obj !== 'object') return

        // Prevent circular references
        if (visited.has(obj)) return
        visited.add(obj)

        // Check if current object is a VickyProductCarouselBlock via ContentLink
        if (obj.ContentLink?.Expanded?.__typename === 'VickyProductCarouselBlock') {
            vickyBlocks.push({
                ...obj.ContentLink.Expanded,
                _path: path,
                _context: 'ContentLink'
            })
            // Continue traversing to find nested blocks within this one
        }

        // Check for direct __typename (for items already expanded)
        if (obj.__typename === 'VickyProductCarouselBlock') {
            vickyBlocks.push({
                ...obj,
                _path: path,
                _context: 'direct'
            })
            // Continue traversing to find nested blocks within this one
        }

        // Special handling for common Optimizely CMS structures
        const specialKeys = [
            'MainRegion', 'BottomArea', 'items', 'carouselItems', 'ContainerPageMainRegion',
            'ContentBlocks', '_Content', 'data', 'page', 'Areas'
        ]

        // Recursively check arrays and objects
        if (Array.isArray(obj)) {
            obj.forEach((item, index) => traverse(item, `${path}[${index}]`))
        } else {
            // Prioritize special CMS keys first
            specialKeys.forEach(key => {
                if (obj[key] !== undefined) {
                    traverse(obj[key], path ? `${path}.${key}` : key)
                }
            })

            // Then check all other properties
            Object.entries(obj).forEach(([key, value]) => {
                if (!specialKeys.includes(key)) {
                    traverse(value, path ? `${path}.${key}` : key)
                }
            })
        }
    }

    traverse(content)


    return vickyBlocks
}

/**
 * Create a mapping between VickyAI carousel blocks and their required data types
 *
 * @param {Array} vickyBlocks - Array of VickyProductCarouselBlock objects
 * @returns {Object} Mapping of recommender types to required data
 */
export const createVickyDataMapping = (vickyBlocks) => {
    const mapping = {
        recommendations: [],
        similars: [],
        boughtTogether: []
    }

    vickyBlocks.forEach((block, index) => {
        // Support both RecommenderType and AIModel properties for backward compatibility
        const recommenderType = (block.RecommenderType || block.AIModel)?.toLowerCase() || 'recommendations'

        // Normalize recommender type variants
        let normalizedType = 'recommendations'
        if (['similars', 'similar'].includes(recommenderType)) {
            normalizedType = 'similars'
        } else if (['bought-together', 'boughttogether', 'frequently-bought-together'].includes(recommenderType)) {
            normalizedType = 'boughtTogether'
        }

        mapping[normalizedType].push({
            blockIndex: index,
            block: block,
            id: `vicky-block-${normalizedType}-${index}`
        })
    })

    return mapping
}

/**
 * Get appropriate VickyAI data for a specific carousel block based on its recommender type
 *
 * @param {Object} block - VickyProductCarouselBlock object
 * @param {Object} vickyData - Object containing all VickyAI data {recommendations, similars, boughtTogether}
 * @returns {Object} {products: Array, isLoading: boolean}
 */
export const getVickyDataForBlock = (block, vickyData) => {
    // Support both RecommenderType and AIModel properties for backward compatibility
    const recommenderType = (block.RecommenderType || block.AIModel)?.toLowerCase() || 'recommendations'

    // If vickyData is directly a products array (for empty cart 'recommendations')
    if (Array.isArray(vickyData)) {
        return {
            products: vickyData,
            isLoading: false
        }
    }

    // Map recommender type to data source
    if (['similars', 'similar'].includes(recommenderType)) {
        return {
            products: vickyData.similars?.products || [],
            isLoading: vickyData.similars?.isLoading || false
        }
    } else if (['bought-together', 'boughttogether', 'frequently-bought-together'].includes(recommenderType)) {
        return {
            products: vickyData.boughtTogether?.products || [],
            isLoading: vickyData.boughtTogether?.isLoading || false
        }
    } else {
        // Default to recommendations
        return {
            products: vickyData.recommendations?.products || [],
            isLoading: vickyData.recommendations?.isLoading || false
        }
    }
}

/**
 * Enhanced content processor that injects VickyAI data into carousel layouts
 * Handles multiple VickyAI blocks and different carousel structures
 *
 * @param {Object} content - Optimizely CMS content object
 * @param {Object} vickyData - VickyAI data object
 * @returns {Object} Modified content with VickyAI data injected
 */
export const injectVickyDataIntoContent = (content, vickyData) => {
    if (!content || !vickyData) return content

    // Track processed blocks to avoid duplication
    const processedBlockIds = new Set()

    // Helper function to recursively inject VickyAI data
    const injectData = (obj, depth = 0) => {
        if (!obj || typeof obj !== 'object' || depth > 10) return obj // Prevent deep recursion

        // Handle arrays
        if (Array.isArray(obj)) {
            return obj.map(item => injectData(item, depth + 1))
        }

        // Handle objects
        const result = {}
        Object.keys(obj).forEach(key => {
            const value = obj[key]

            // Special handling for CarouselLayout components
            if (key === '__typename' && value === 'CarouselLayout') {
                // Look for VickyProductCarouselBlock in carouselItems
                if (obj.carouselItems) {
                    const vickyBlocks = findVickyCarouselBlocks({items: obj.carouselItems})
                    if (vickyBlocks.length > 0) {
                        // Find the first unprocessed VickyAI block
                        const unprocessedBlock = vickyBlocks.find(block => {
                            const recommenderType = block.RecommenderType || block.AIModel
                            const blockId = `${block.Title}-${recommenderType}-${block._path}`
                            return !processedBlockIds.has(blockId)
                        })

                        if (unprocessedBlock) {
                            const recommenderType = unprocessedBlock.RecommenderType || unprocessedBlock.AIModel
                            const blockId = `${unprocessedBlock.Title}-${recommenderType}-${unprocessedBlock._path}`
                            processedBlockIds.add(blockId)

                            // Inject VickyAI data for this specific block
                            const blockData = getVickyDataForBlock(unprocessedBlock, vickyData)
                            result.vickyAIData = {
                                ...blockData,
                                _blockInfo: {
                                    title: unprocessedBlock.Title,
                                    recommenderType: recommenderType,
                                    path: unprocessedBlock._path
                                }
                            }

                        }
                    }
                }
            }

            // Special handling for direct VickyProductCarouselBlock items
            if (key === '__typename' && value === 'VickyProductCarouselBlock') {
                const recommenderType = obj.RecommenderType || obj.AIModel
                const blockId = `${obj.Title || obj.CarouselTitle}-${recommenderType}-direct`
                if (!processedBlockIds.has(blockId)) {
                    processedBlockIds.add(blockId)

                    // Inject VickyAI data directly into the block
                    const blockData = getVickyDataForBlock(obj, vickyData)
                    result._recommendationData = blockData
                }
            }

            result[key] = injectData(value, depth + 1)
        })

        return result
    }

    return injectData(content)
}